import axios, { AxiosError, AxiosInstance, AxiosResponse } from 'axios';
import { cloneDeep, merge } from 'lodash';

import { useDebounce } from '@SBGSports/referee-react';
import {
    ActivitiesApi,
    DeleteActivitiesRequest,
    ReprocessActivitiesRequest,
    RestoreActivitiesRequest,
    UpdateActivitiesUserRequest,
} from 'oas';

import { InfiniteData, QueryKey, UseMutationOptions, UseMutationResult, useQueryClient } from '@tanstack/react-query';
import { sanitisePeriodAthletes } from 'activity-editor/support/activity-sanitisation';
import {
    ActivitiesApi as ActivitiesInternalApi,
    ActivityBatchResponse,
    ActivityResponse,
    ActivityTeamsApi,
    ActivityUpdateBatchRequest,
    ActivityUpdateRequestPeriod,
    Configuration,
    CopyActivityRequest,
    MoveAthleteActivityRequest,
    MoveAthleteActivityResponse,
    Rotation,
    RotationsApi,
} from 'oas/internal';
import { useApiMutation } from '../use-api-mutation';
import { API_NAMES } from '../constants';
import { useGetActivity } from './get';
import { TfActivity } from './transform';

export const useReprocessActivity = (): UseMutationResult<void, unknown, ReprocessActivitiesRequest> => {
    return useApiMutation(async (attr, apiConfig, axiosInstance) => {
        await new ActivitiesApi(apiConfig, '', axiosInstance).reprocessActivities(attr);
    });
};

export const useDeleteActivities = (): UseMutationResult<void, unknown, DeleteActivitiesRequest> => {
    const queryClient = useQueryClient();

    return useApiMutation(
        async (attr, apiConfig, axiosInstance) => {
            await new ActivitiesApi(apiConfig, '', axiosInstance).deleteActivities(attr);
        },
        {
            onSettled: () => {
                queryClient.invalidateQueries({
                    queryKey: ['activities'],
                });
            },
        },
    );
};

export const useRestoreActivities = (): UseMutationResult<void, unknown, RestoreActivitiesRequest> => {
    const queryClient = useQueryClient();

    return useApiMutation(
        async (attr, apiConfig, axiosInstance) => {
            await new ActivitiesApi(apiConfig, '', axiosInstance).restoreActivities(attr);
        },
        {
            onSettled: () => {
                queryClient.invalidateQueries({
                    queryKey: ['activities'],
                });
            },
        },
    );
};

export const useUpdateActivitiesUser = (): UseMutationResult<
    void,
    unknown,
    UpdateActivitiesUserRequest & { userId: string }
> => {
    const queryClient = useQueryClient();

    return useApiMutation(
        async (attr, apiConfig, axiosInstance) => {
            await new ActivitiesApi(apiConfig, '', axiosInstance).updateActivitiesUser(attr.userId, attr);
        },
        {
            onSettled: () => {
                queryClient.invalidateQueries({
                    queryKey: ['activities'],
                });
            },
        },
    );
};

export const useUpdateActivityName = (): UseMutationResult<void, unknown, { id: string; name: string }> => {
    const queryClient = useQueryClient();

    return useApiMutation(
        async (attr, apiConfig, axiosInstance) => {
            const response = await new ActivitiesApi(apiConfig, '', axiosInstance).updateActivity(attr.id, {
                name: attr.name,
            });
            if (axios.isAxiosError(response)) {
                throw response;
            }
        },
        {
            onSuccess: (_data, newAttr) => {
                if (!newAttr) {
                    return;
                }

                // Update this activity in the dataset of every cached query
                const dataSets = queryClient.getQueriesData<InfiniteData<TfActivity[]>>({
                    queryKey: ['activities'],
                });

                for (let i = 0; i < dataSets.length; i++) {
                    const dataSet = dataSets[i][1];
                    if (dataSet?.pages) {
                        for (let j = 0; j < dataSet?.pages.length; j++) {
                            dataSet.pages[j] = dataSet.pages[j].map((activity) => {
                                if (activity.id === newAttr.id) {
                                    return { ...activity, ...newAttr };
                                }
                                return activity;
                            });
                        }
                    }
                }
            },
            onSettled: () => {
                queryClient.invalidateQueries({
                    queryKey: ['activities'],
                });
            },
        },
    );
};

export const useUpdateBatchActivity = (
    options?: UseMutationOptions<
        AxiosResponse<ActivityBatchResponse>,
        unknown,
        ActivityUpdateBatchRequest & { id: string }
    >,
): UseMutationResult<AxiosResponse<ActivityBatchResponse>, unknown, ActivityUpdateBatchRequest & { id: string }> => {
    const queryClient = useQueryClient();
    return useApiMutation(
        async (attr, apiConfig, axiosInstance) => {
            const { id, ...rest } = attr;
            const response = await new ActivitiesInternalApi(apiConfig, '', axiosInstance).activityUpdateBatch(
                id,
                rest,
            );

            if (axios.isAxiosError(response)) {
                throw response;
            } else {
                return response;
            }
        },
        {
            onSuccess: (_data, variables) => {
                queryClient.invalidateQueries({
                    queryKey: ['activities', variables.id],
                });
                queryClient.invalidateQueries({
                    queryKey: ['activities', 'athletes', [variables.id]],
                });
            },
            ...options,
        },
        [],
        'internal',
        API_NAMES.ACTIVITY_SERVICE,
    );
};

export const useUpdateBatchActivityForAutosave = (
    invalidateIncludes?: Parameters<typeof useGetActivity>[1],
    options?: UseMutationOptions<
        AxiosResponse<ActivityBatchResponse>,
        unknown,
        ActivityUpdateBatchRequest & { id: string }
    >,
): UseMutationResult<AxiosResponse<ActivityBatchResponse>, unknown, ActivityUpdateBatchRequest & { id: string }> => {
    const queryClient = useQueryClient();

    const debouncedInvalidate = useDebounce((id: string) => {
        queryClient.invalidateQueries({
            queryKey: ['activities', id],
        });
        queryClient.invalidateQueries({
            queryKey: ['activities', 'athletes', [id]],
        });
    }, 5000);

    return useUpdateBatchActivity({
        onMutate: async (newAttr: ActivityUpdateBatchRequest & { id: string }) => {
            debouncedInvalidate(newAttr.id);

            let key: QueryKey = ['activities', newAttr.id];
            if (invalidateIncludes) {
                key = [...key, invalidateIncludes];
            }

            // Cancel any outgoing refetches (so they don't overwrite our optimistic update)
            await queryClient.cancelQueries({
                queryKey: key,
            });

            // Snapshot the previous value
            const oldActivity = cloneDeep(queryClient.getQueryData<TfActivity>(key));
            if (oldActivity !== undefined) {
                // Optimistically update to the new value
                const newActivity = merge(oldActivity, newAttr) as TfActivity;
                newActivity.periods = newAttr.periods?.map((period: ActivityUpdateRequestPeriod) => {
                    const oldPeriod = oldActivity.periods?.find((p) => p.id === period.id);

                    const athletes =
                        period.athletes?.map((pa) => ({
                            athlete_id: pa.athlete_id,
                            rotations:
                                oldPeriod?.period_athletes?.find((x) => x.athlete_id === pa.athlete_id)?.rotations ??
                                [],
                            is_injected: 0,
                        })) ?? [];
                    const cleanedAthletes = sanitisePeriodAthletes(athletes);

                    // Update athletes
                    return {
                        ...period,
                        period_athletes: cleanedAthletes,
                        start_time: new Date(period.start_time_ms),
                        end_time: new Date(period.end_time_ms),
                        is_injected: 0,
                        tags: oldPeriod?.tags ?? [],
                    };
                });

                queryClient.setQueryData(key, newActivity);
            }
        },
        // If the mutation fails, invalidate the query
        onError: (_err, newAttr) => {
            let key: QueryKey = ['activities', newAttr.id];
            if (invalidateIncludes) {
                key = [...key, invalidateIncludes];
            }

            queryClient.invalidateQueries({
                queryKey: key,
            });
        },
        onSuccess: (_data, variables) => {
            debouncedInvalidate(variables.id);
        },
        ...options,
    });
};

export const useUpdateAthleteRotations = (
    invalidateIncludes?: Parameters<typeof useGetActivity>[1],
    options?: UseMutationOptions<
        AxiosResponse<Rotation[]>,
        unknown,
        { activity_id: string; period_id: string; athlete_id: string; rotations: Rotation[] }
    >,
): UseMutationResult<
    AxiosResponse<Rotation[]>,
    unknown,
    { activity_id: string; period_id: string; athlete_id: string; rotations: Rotation[] }
> => {
    const queryClient = useQueryClient();
    // Key used to track pending mutations for this query
    const mutationKey: QueryKey = ['useUpdateAthleteRotations'];

    /**
     *
     * Get a filtered list of pending mutations for this query and invalidate the query if there are no more pending mutations
     *
     * The getMutationCache will return the full list of mutations that we need to filter out only the relevant ones
     *
     * @param key - The query key to invalidate
     */
    const invalidateQuery = (key: QueryKey) => {
        // Get list of pending mutations for this query filtered by the key
        const mutateQueries = queryClient
            .getMutationCache()
            .findAll({ mutationKey: mutationKey })
            .filter((item) => item.state.status === 'idle' || item.state.status === 'pending');

        // Because this is getting called from onSuccess and onError there will still be the count equal to 1
        if (mutateQueries.length === 1) {
            queryClient.invalidateQueries(
                invalidateIncludes ? { queryKey: [...key, invalidateIncludes] } : { queryKey: key },
            );
        }
    };

    return useApiMutation(
        async (
            { activity_id, period_id, athlete_id, rotations },
            apiConfig,
            axiosInstance,
        ): Promise<AxiosResponse<Rotation[]>> => {
            const response = await new RotationsApi(apiConfig, '', axiosInstance).updateRotations(
                activity_id,
                period_id,
                athlete_id,
                rotations,
            );

            if (axios.isAxiosError(response)) {
                throw response;
            } else {
                return response;
            }
        },
        {
            onSuccess: (_data, variables) => {
                invalidateQuery(['activities', variables.activity_id]);
            },
            onError: (_error: AxiosError, variables) => {
                if (_error.response?.status === 404) {
                    invalidateQuery(['activities', variables.activity_id]);
                }
            },
            ...{ ...options, retry: 5 },
        },
        mutationKey,
        'internal',
        API_NAMES.ACTIVITY_SERVICE,
    );
};

export const useUpdateActivityVenueField = (): UseMutationResult<
    void,
    unknown,
    { id: string; name: string; venue_field_id: string }
> => {
    const queryClient = useQueryClient();

    return useApiMutation(
        async (attr, apiConfig, axiosInstance) => {
            const response = await new ActivitiesApi(apiConfig, '', axiosInstance).updateActivity(attr.id, {
                name: attr.name,
                venue_field_id: attr.venue_field_id,
            });
            if (axios.isAxiosError(response)) {
                throw response;
            }
        },
        {
            onSuccess: (_data, variables) => {
                queryClient.invalidateQueries({
                    queryKey: ['activities', variables.id],
                });
                queryClient.invalidateQueries({
                    queryKey: ['activities', 'deep'],
                });
            },
        },
    );
};

export const useCreateActivityFromExisting = (): UseMutationResult<
    ActivityResponse,
    unknown,
    { currentActivityId: string; activityRequest: CopyActivityRequest }
> => {
    const queryClient = useQueryClient();

    return useApiMutation(
        async (
            { currentActivityId, activityRequest }: { currentActivityId: string; activityRequest: CopyActivityRequest },
            apiConfig: Configuration,
            axiosInstance: AxiosInstance,
        ): Promise<ActivityResponse> => {
            const response = await new ActivitiesInternalApi(apiConfig, '', axiosInstance).createActivityCopy(
                currentActivityId,
                activityRequest,
            );

            if (axios.isAxiosError(response)) {
                throw response;
            }

            return response.data;
        },
        {
            onSettled: () => {
                queryClient.invalidateQueries({
                    queryKey: ['activities'],
                });
            },
        },
        [],
        'internal',
        API_NAMES.ACTIVITY_SERVICE,
    );
};
export const useMoveAthletesToActivity = (): UseMutationResult<
    AxiosResponse<MoveAthleteActivityResponse>,
    unknown,
    { currentActivityId: string; moveAthletesActivityRequest: MoveAthleteActivityRequest },
    unknown
> => {
    const queryClient = useQueryClient();

    return useApiMutation(
        async (
            { currentActivityId, moveAthletesActivityRequest: moveAthleteActivityRequest },
            apiConfig,
            axiosInstance,
        ): Promise<AxiosResponse<MoveAthleteActivityResponse>> => {
            // Call the API to move the athlete
            const response = await new ActivitiesInternalApi(
                apiConfig,
                '',
                axiosInstance,
            ).moveAthletesToAnotherActivity(currentActivityId, moveAthleteActivityRequest);

            // Throw if its an error response
            if (axios.isAxiosError(response)) {
                throw response;
            }

            return response; // Return the entire response
        },
        {
            onSettled: () => {
                queryClient.invalidateQueries({
                    queryKey: ['activities'],
                });
                queryClient.invalidateQueries({
                    queryKey: ['athletes'],
                });

                queryClient.invalidateQueries({
                    queryKey: ['annotations'],
                });
            },
        },
        [],
        'internal',
    );
};

export const useCreateActivityTeam = (): UseMutationResult<
    AxiosResponse<string>,
    unknown,
    { activityId: string; teamId: string },
    unknown
> => {
    const queryClient = useQueryClient();

    return useApiMutation(
        async ({ activityId, teamId }, apiConfig, axiosInstance): Promise<AxiosResponse<string>> => {
            const response = await new ActivityTeamsApi(apiConfig, '', axiosInstance).createActivityTeam(
                activityId,
                teamId,
            );

            if (axios.isAxiosError(response)) {
                throw response;
            }

            return response;
        },
        {
            onSuccess: (_data, variables) => {
                queryClient.invalidateQueries({ queryKey: ['activities', variables.activityId] });
                queryClient.invalidateQueries({ queryKey: ['activities', 'teams'] });
            },
        },
        [],
        'internal',
        API_NAMES.ACTIVITY_SERVICE,
    );
};
